import React, { useState, useRef, FunctionComponent, useEffect } from 'react'
import { usePut, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { Form } from 'react-bootstrap'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import GenericModal from '../../../../components/component-items/modal'

// Cards
import { Condition } from './condition'
import { Actions } from './action'
import { Event } from './event'

// Types

// Utils
import { filterActions } from './utils'

export const AutomationEditModal: FunctionComponent<{
  setOpen: (open: boolean) => void
  automation: any
  productVariantOptions?: any[]
}> = ({ setOpen, automation, productVariantOptions = [] }) => {
  const {
    state: { csrf, userIsWarehouse },
  } = globalState()

  const node = useRef<HTMLFormElement | null>(null)
  const [validated, setValidated] = useState(false)
  const [saving, setSaving] = useState(false)

  const [automationType, setAutomationType] = useState('')
  const [filters, setFilters] = useState<any>(null)
  const [event, setEvent] = useState<any>(null)
  const [actions, setActions] = useState<any>(null)
  const [automationName, setAutomationName] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)

  useEffect(() => {
    // Use the existing automation data directly
    setAutomationType(automation.category)
    setFilters(automation.condition)
    setEvent(automation.event)
    setActions(automation.action)
    setAutomationName(automation.name)
    setIsPrivate(automation.isPrivate)
  }, [automation])

  const checkValidity = () => {
    setValidated(true)
    if (node?.current?.checkValidity() === false) {
      return false
    }
    return true
  }

  const handleSave = async (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    if (!checkValidity()) {
      return
    }

    setSaving(true)
    let action = filterActions(actions)

    try {
      const response = await usePut(
        `/api/core/automation/${automation.id}/`,
        {
          ...{
            category: automationType,
            condition: filters,
            event,
            action,
            name: automationName,
            isPrivate,
          },
        },
        csrf,
        false,
        true,
        true
      )

      if (response?.error) {
        notify({ type: 'warning', message: response?.error })
      } else {
        notify({ type: 'success', message: 'Automation Updated Successfully' })
        setOpen(false)
        // Refresh the page to show updated data
        window.location.reload()
      }
    } catch (error) {
      notify({ type: 'error', message: 'Failed to update automation' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <GenericModal
        heading={`Edit Automation: ${automationName || 'Loading...'}`}
        show
        onHide={() => setOpen(false)}
        size="xl"
        buttons={
          <>
            <GlobalStyles.Button onClick={() => setOpen(false)}>Cancel</GlobalStyles.Button>
            <GlobalStyles.Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </GlobalStyles.Button>
          </>
        }
      >
        <Form ref={node} validated={validated} onSubmit={handleSave} style={{ padding: '1em' }}>
          <div style={{ padding: '1em 0' }}>
            {!automation || !automation.id || !filters || !event || !actions ? (
              <div style={{ textAlign: 'center', padding: '2em' }}>
                <p>Loading automation data...</p>
              </div>
            ) : (
              <>
                {/* Automation Type and Name - Side by Side */}
                <div style={{ marginBottom: '2em', padding: '1em', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div className="row">
                    {/* Automation Type - Left Column */}
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Automation Type</Form.Label>
                        <Form.Control
                          type="text"
                          value={automationType.charAt(0).toUpperCase() + automationType.slice(1).replace(/_/g, ' ')}
                          disabled
                          readOnly
                          style={{ backgroundColor: '#e9ecef' }}
                        />
                        <Form.Text className="text-muted">Cannot be changed after creation</Form.Text>
                      </Form.Group>
                    </div>

                    {/* Automation Name - Right Column */}
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Automation Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Example: 'Pause new orders with tag 'hold'"
                          value={automationName}
                          required
                          onChange={(e: any) => setAutomationName(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  {/* Privacy Setting - Full Width Below */}
                  {userIsWarehouse && (
                    <GlobalStyles.Tooltip data-title="This will only be visible to executives">
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5em' }}>
                        <Form.Check
                          type="checkbox"
                          label="Private"
                          checked={isPrivate}
                          onChange={() => setIsPrivate(!isPrivate)}
                        />
                      </div>
                    </GlobalStyles.Tooltip>
                  )}
                </div>

                <h3 style={{ marginTop: '1em' }}>Condition</h3>
                <StatusBoxStyled className={'blue'} style={{ margin: '0 0.9em', textAlign: 'center' }}>
                  Please note: Automations apply to all warehouses unless specified.
                </StatusBoxStyled>
                <div style={{ display: 'flex' }}>
                  <Condition {...{ automationType, filters, setFilters }} r={240} g={248} b={255} />
                </div>

                <h3 style={{ marginTop: '1em' }}>Trigger Event</h3>
                <div style={{ display: 'flex' }}>
                  <Event {...{ automationType, event, setEvent }} />
                </div>

                <h3 style={{ marginTop: '1em' }}>Action</h3>
                {automationType === 'order' && (
                  <StatusBoxStyled className={'blue'} style={{ margin: '0 0.9em', textAlign: 'center' }}>
                    Please note: all modifications will only be performed on unfulfilled orders.
                  </StatusBoxStyled>
                )}
                <Actions
                  {...{ automationType, filters, event, actions, setActions }}
                  r={240}
                  g={248}
                  b={255}
                  initialProductVariantOptions={productVariantOptions}
                />
              </>
            )}
          </div>
        </Form>
      </GenericModal>
    </>
  )
}

AutomationEditModal.displayName = 'AutomationEditModal'
